import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import startCase from 'lodash/startCase'
import Layout from 'containers/Layout'
import moment from 'moment'
import { castArray, get, isString, orderBy, some, split, trim, truncate } from 'lodash'
import { getCountryDisplayName } from 'pages/company/modals/countries'
import apiGet from 'lib/network/apiGet'
import apiPut from 'lib/network/apiPut'

import { useHistory, useParams } from 'react-router-dom'

import { OfficeBuildingIcon } from '@heroicons/react/outline'

import Table from 'components/pageComponents/Table'
import LoadingIcon from 'components/atoms/LoadingIcon'
import EmptyState from 'components/pageComponents/EmptyState'
import Button from 'components/atoms/Button'
import Switch from 'components/atoms/Switch'
import { startEndTruncate, accessibleOnClick } from 'utils'
import { useDispatch, useSelector } from 'react-redux'
import { Tooltip } from 'antd'
import { IoCopyOutline, IoSearch } from 'react-icons/io5'
import LanguageSwitcher from 'components/atoms/LanguageSwitcher'
import notify from 'components/atoms/Notify'
import PageFooter from 'components/paginationComponent'

const Companies = () => {
  const { t } = useTranslation()
  const history = useHistory()

  const [loading, setLoading] = useState(false)
  const [filter, setFilter] = useState({ workspace: -1 })
  const [companies, setCompanies] = useState([])
  const [pagination, setPagination] = useState({})
  const [search, setSearch] = useState('')

  const { id } = useParams()

  useEffect(() => {
    fetchCompanies()
  }, [search, filter])

  const fetchCompanies = async (page = 1) => {
    if (page === 1) {
      setLoading(true)
    }
    const params = {}
    params.pageNumber = page
    params.pageSize = 20
    params.query = search || undefined
    if (filter.sortBy && filter.sortOrder) {
      params.sortBy = filter.sortBy
      params.sortOrder = filter.sortOrder
    }
    const response = await apiGet('companies', params)

    setCompanies(page === 1 ? response.data.company : [...companies, ...response.data.company])
    setPagination(response?.data?.pagination || {})

    setLoading(false)
  }

  const user = useSelector(state => get(state, 'user.user'))
  const hasAdminAccess = some(user?.roles, { type: 'admin' })

  const handleCompanyStatusChange = async (companyId, isActive) => {
    try {
      const response = await apiPut(`company/status/${companyId}`, { isActive })

      if (response?.success) {
        setCompanies(prevCompanies =>
          prevCompanies.map(company => (company._id === companyId ? { ...company, isActive, needsReview: isActive ? false : company.needsReview } : company))
        )
        notify.success(t('companies.messages.companyStatusUpdated'))
      }
    } catch (error) {
      notify.error(t('companies.messages.failedToUpdateCompanyStatus'))
    }
  }

  const copyText = text => {
    navigator.clipboard.writeText(text).then(() => {
      notify.success('Copied to clipboard', truncate(text, { length: 35 }))
    })
  }

  const columns = [
    {
      name: t('companies.columns.name'),
      width: '10%',
      key: 'name',
      sortable: true,
      rendered: item => {
        const avatarAndName = (
          <>
            <div className="flex-shrink-0 h-10 w-10">
              {item?.logoThumbnailUrl ? (
                <img className="h-10 w-10 rounded-full object-cover" src={item?.logoThumbnailUrl} alt="" />
              ) : (
                <div className="h-10 w-10 rounded-full bg-gray-200 flex items-center justify-center shrink-0" aria-hidden>
                  <OfficeBuildingIcon className="h-5 w-5 text-gray-500" strokeWidth={1.75} />
                </div>
              )}
            </div>
            <div className="ml-4">
              <div className="text-sm text-normal text-gray-900">
                {item?.name || 'User'}
                {item?.needsReview && (
                  <span className="ml-2 inline-flex rounded-full bg-yellow-100 px-2 py-0.5 text-xs font-medium text-yellow-800">{t('companySignup.review.badge')}</span>
                )}
              </div>
              <div
                onClick={e => {
                  e.stopPropagation()
                  e.preventDefault()
                }}
                className="text-[13px] text-gray-500 flex"
              ></div>
            </div>
          </>
        )
        if (hasAdminAccess) {
          return (
            <a href={`/company/edit/${item._id}/company-profile`} className="flex items-center cursor-pointer no-underline">
              {avatarAndName}
            </a>
          )
        }
        return <div className="flex items-center">{avatarAndName}</div>
      },
    },
    {
      width: '10%',
      name: t('companies.columns.governorate'),
      key: 'governorate',
      sortable: true,
      rendered: item => {
        return (
          <>
            <div>
              <span className="text-[13px]">{item.governorate}</span>
            </div>
          </>
        )
      },
    },

    {
      width: '15%',
      name: t('companies.columns.maxAgeMinAge'),
      key: 'Age',
      sortable: true,
      rendered: item => (
        <span className="text-[13px]">
          {
            // eslint-disable-next-line
            orderBy(item?.roles?.filter(d => d.name !== 'platform_admin')?.map(r => startCase(r.name)))?.map(d => (
              <span className="border mr-3 shadow-sm rounded-2xl px-2 py-0.5" key={d}>
                {d}
              </span>
            ))
          }
          <span className="border mr-3 shadow-sm rounded-2xl px-2 py-0.5">
            {' '}
            {startCase(item.maxAge)}/{startCase(item.minAge)}
          </span>
        </span>
      ),
    },
    {
      width: '10%',
      name: t('companies.columns.totalCars'),
      key: 'rentalCarCount',
      sortable: true,
      rendered: item => {
        return (
          <>
            <div>
              <span className="text-[13px]">{item.rentalCarCount}</span>
            </div>
          </>
        )
      },
    },

    {
      width: '10%',
      name: t('companies.columns.blockedCountries'),
      key: 'blockedCountries',
      sortable: true,
      rendered: item => (
        <span className="text-[13px] max-w-[50px]">
          {
            // eslint-disable-next-line
            orderBy(item?.roles?.filter(d => d.name !== 'platform_admin')?.map(r => startCase(r.name)))?.map(d => (
              <span className="border mr-3 shadow-sm rounded-2xl px-2 py-0.5" key={d}>
                {d}
              </span>
            ))
          }
          {castArray(item.blockedCountries)
            .flatMap(country => (isString(country) ? split(country, ',') : country))
            .map(country => trim(String(country)))
            .filter(Boolean)
            .map(countryCode => (
              <span className="border mr-3 shadow-sm rounded-2xl px-2 py-0.5" key={countryCode}>
                {getCountryDisplayName(countryCode)}
              </span>
            ))}
        </span>
      ),
    },

    {
      width: '5%',
      name: t('companies.columns.created'),
      key: 'created',
      sortable: true,
      rendered: item => {
        const createdTime = get(item, 'createdAt', '')
        const createdAt = createdTime ? moment(createdTime).fromNow() : 'Registered'
        return createdAt
      },
    },
    ...(hasAdminAccess
      ? [
          {
            width: '5%',
            name: t('companies.columns.status'),
            key: 'isActive',
            sortable: false,
            rendered: item => (
              <div className="flex items-center space-x-2">
                <Switch value={item.isActive !== false} onChange={isActive => handleCompanyStatusChange(item._id, isActive)} />
              </div>
            ),
          },
        ]
      : []),
  ]

  return (
    <Layout
      showLogo={!!id}
      breadcrumbs={[{ name: t('companies.breadcrumb'), pathname: '/companies' }]}
      headerRight={
        <div className="flex flex-col sm:flex-row items-start sm:items-center w-full justify-end sm:mt-0 sm:ml-4 gap-2 sm:gap-4">
          <div className="flex items-center gap-2 w-full sm:w-auto">
            <PageFooter currentPage={pagination.currentPage} totalPages={pagination.totalPages} totalRecords={pagination.totalRecords} />
            <div className="mt-1 relative rounded-md shadow-sm flex-1 sm:flex-none">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" aria-hidden="true">
                <IoSearch className="mr-3 h-4 w-4 text-gray-400" aria-hidden="true" />
              </div>
              <input
                onChange={e => setSearch(e.target.value)}
                value={search}
                type="text"
                name="search"
                id="search"
                className="block w-full pl-9 shadow-none sm:text-sm bg-gray-100 border-0 focus:outline-none rounded-sm"
                placeholder={t('companies.search.placeholder')}
              />
            </div>
          </div>
          {user.isWorkspaceAdmin ? (
            <Button onClick={() => history.push('/users/add')} className="w-full sm:w-auto">
              {t('companies.buttons.inviteUser')}
            </Button>
          ) : (
            hasAdminAccess && (
              <Button onClick={() => history.push('/add/company')} className="w-full sm:w-auto">
                {t('companies.buttons.addCompany')}
              </Button>
            )
          )}
        </div>
      }
      title={t('companies.title')}
    >
      <div className="flex flex-col">
        <div className="flex-1 flex items-stretch">
          <main className="flex-1">
            <div className="align-middle inline-block min-w-full">
              <div className="min-w-full">
                {!loading ? (
                  <>
                    {companies?.length === 0 ? (
                      <EmptyState v2 description={t('companies.emptyState.description')} />
                    ) : (
                      <Table
                        hasMore={pagination?.currentPage !== pagination?.totalPages}
                        fetchData={() => fetchCompanies(pagination?.currentPage + 1)}
                        sortBy={filter.sortBy}
                        sortOrder={filter.sortOrder}
                        handleSort={(sortBy, sortOrder) => {
                          setFilter(prevFilter => ({ ...prevFilter, sortBy, sortOrder }))
                        }}
                        data={companies}
                        columns={columns}
                      />
                    )}
                  </>
                ) : (
                  <div className="w-full h-[200px] border-0 flex justify-center items-center">
                    <LoadingIcon />
                  </div>
                )}
              </div>
            </div>
          </main>
        </div>
      </div>
    </Layout>
  )
}

export default Companies
